import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator } from "@frui.ts/validation";
import { Modules } from "config";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import DistributorRepository from "repositories/distributorRepository";
import type Distributor from "entities/distributor";
import { DistributorValidationRules } from "entities/distributor";
import DistributorType from "entities/distributorType";
import DistributorStatus from "entities/distributorStatus";
import EditableDetailViewModel from "../../../editableDetailViewModel";

@Router.registerRoute({ name: Router.Self, route: ":distributorId" })
export default class DistributorDetailViewModel extends EditableDetailViewModel<Distributor> {
  readonly modelName = "distributor";

  constructor(
    protected itemId: string,
    protected repository: DistributorRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    private optionsService: OptionsService
  ) {
    super(itemId);
  }

  protected async loadDetail() {
    const item = await this.repository.get(this.itemId);
    attachAutomaticValidator(item, DistributorValidationRules);

    return item;
  }

  updateData(): Promise<Distributor> {
    if (this.item.email == "") {
      const distributorCopy = Object.assign({}, this.item) as any;
      delete distributorCopy.email;
      return this.repository.update(distributorCopy);
    }
    return this.repository.update(this.item);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `distributorManagement.${code}`, size);
  }

  @computed
  get distributorTypeOptions() {
    return this.optionsService.internalEnumOptions(DistributorType);
  }

  @computed
  get distributorStatusOptions() {
    return this.optionsService.internalEnumOptions(DistributorStatus);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new DistributorDetailViewModel(
        itemId,
        container.get(DistributorRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(OptionsService)
      );
  }
}
